import { useMemo } from "react";
import { LifeBuoy, MessageCircle, Clock, HelpCircle, Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent } from "@/components/ui/card";

// LINE友だち追加URL
const LINE_URL = "https://lin.ee/8kH6TiM";
// 爆速サポート 電話番号
const SUPPORT_TEL = "03-3855-1610";

const STORE_FAQ = [
  {
    q: "発注の締切はいつですか？",
    a: "納品週の前週・指定曜日の設定時刻が発注締切です。締切までは自由に数量変更・追加・削除ができます。締切後〜配送前日12:00までは「発注確認＆変更」から変更申請を送っていただき、管理者の承認で反映されます。前日12:00を過ぎると変更できません。",
  },
  {
    q: "締切後に数量を間違えたことに気づきました。どうすればいいですか？",
    a: "「発注確認＆変更」から変更申請を送信してください。管理者が承認すると反映されます。前日12:00を過ぎている場合はシステム上変更できないため、爆速サポート（TEL 03-3855-1610）またはLINEへご連絡ください。",
  },
  {
    q: "リストに無い商品を注文できますか？",
    a: "「リストなし発注」から商品名・数量を入力して発注できます（発注締切前の納品日のみ）。",
  },
  {
    q: "発注内容の控えを印刷したいです。",
    a: "「発注控え印刷」から、店舗×納品日ごとの控えを印刷できます。",
  },
  {
    q: "同じ商品を用途別に分けて発注したいです。",
    a: "発注入力画面で行を分割し、使用日ごとに別々の数量として登録できます。合算されず別明細として保持されます。",
  },
];

const ADMIN_FAQ = [
  {
    q: "「要対応ボックス」とは何ですか？",
    a: "締切を過ぎた配送分のうち、やさまるデスクへ送る前に人の判断が必要なもの（🟠変更承認・🔵注文確定）を、店舗×配送日ごとにまとめた画面です。ここを確認・処理すれば送信前の抜け漏れを防げます。",
  },
  {
    q: "「変更あり（再確定待ち）」と出るのに変更箇所が見当たりません。",
    a: "確定後にお客様が内容を変更した場合に表示されます。実際の変更が無いのに表示が残るケースは自動で解消されるよう改善済みです。気になる場合は一度「再確定する」を押してください。",
  },
  {
    q: "変更申請の承認・却下はどこで行いますか？",
    a: "「要対応ボックス」または「変更申請状況」から、店舗×配送日ごとに承認・却下できます。「まとめて対応」で承認と確定を一度に処理できます。",
  },
  {
    q: "発注締切や変更締切を変更したいです。",
    a: "「配送スケジュール設定」および店舗ごとの設定（発注締切曜日・時刻）で調整できます。事務所休業日は「事務所営業日管理」で設定すると締切が前営業日に自動で繰り上がります。",
  },
  {
    q: "やさまるデスクへの連携はどうなっていますか？",
    a: "やさまるデスク側からのPULL連携で、確定済みの発注が取込対象になります。",
  },
];

export default function SupportPage() {
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const isAdmin = user.role === "admin";
  const faq = isAdmin ? [...ADMIN_FAQ, ...STORE_FAQ] : STORE_FAQ;

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="support-page">
      {/* ヘッダー */}
      <div className="flex items-start gap-3">
        <div className="mt-1 rounded-xl bg-emerald-50 p-2.5">
          <LifeBuoy className="h-6 w-6 text-emerald-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">サポート</h1>
          <p className="text-sm text-slate-500 mt-1">
            よくあるご質問と、困ったときのお問い合わせ窓口をまとめています。
          </p>
        </div>
      </div>

      {/* LINE 爆速サポート */}
      <Card className="border-emerald-100 bg-gradient-to-br from-emerald-50/70 to-white overflow-hidden" data-testid="support-line-card">
        <CardContent className="p-6 flex flex-col sm:flex-row items-center gap-6">
          <div className="flex-shrink-0 rounded-xl bg-white p-3 shadow-sm ring-1 ring-emerald-100">
            <img
              src="/bakusoku-support-qr.png"
              alt="LINE爆速サポート QRコード"
              className="w-40 h-40 object-contain"
              data-testid="support-line-qr"
            />
          </div>
          <div className="text-center sm:text-left">
            <div className="inline-flex items-center gap-2 text-emerald-700 font-semibold">
              <MessageCircle className="h-5 w-5" />
              LINE 爆速サポート
            </div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              操作でお困りのときや、締切を過ぎてしまった急ぎのご相談は、LINEでお気軽にお問い合わせください。
              QRコードを読み取って友だち追加のうえ、メッセージをお送りください。
            </p>
            <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-3 justify-center sm:justify-start">
              <Clock className="h-3.5 w-3.5" />
              営業時間内に順次ご返信します
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4 justify-center sm:justify-start">
              <a
                href={LINE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                data-testid="support-line-button"
              >
                <MessageCircle className="h-4 w-4" />
                LINEで問い合わせる
              </a>
              <a
                href={`tel:${SUPPORT_TEL.replace(/-/g, "")}`}
                className="inline-flex items-center gap-2 rounded-full bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-sm font-semibold px-5 py-2.5 transition-colors"
                data-testid="support-tel-button"
              >
                <Phone className="h-4 w-4" />
                お電話：{SUPPORT_TEL}
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FAQ */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <HelpCircle className="h-5 w-5 text-slate-500" />
          <h2 className="text-lg font-semibold text-slate-900">よくあるご質問</h2>
        </div>
        <Card>
          <CardContent className="p-2 sm:p-4">
            <Accordion type="single" collapsible className="w-full" data-testid="support-faq">
              {faq.map((item, idx) => (
                <AccordionItem key={idx} value={`faq-${idx}`} data-testid={`support-faq-item-${idx}`}>
                  <AccordionTrigger className="text-left text-sm font-medium text-slate-800">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-600 leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
